// Beta-0.5D-B · Unknown Material Queue.
// Sourced from doc_ai_audit where classification == "unknown_material".
// Admins can Map / Ignore / Review Later — auto-creating a material is NOT allowed.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ArrowsClockwise, CircleNotch, Warning, Check, XCircle, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function MapDialog({ row, materials, onClose, onSaved }) {
  const [pick, setPick] = useState(row.suggested_material?.canonical_code || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!pick) { toast.error('割り当て先を選んでください'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/doc-ai/materials/unknown-queue/map`,
        { ocr_value: row.ocr_value, canonical_code: pick },
        { headers: authHeader() });
      toast.success('OCR候補として登録しました');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '登録に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="unknown-map-dialog" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}>
        <p className="text-[13px] font-semibold text-zinc-900 mb-1">既存の材質へマッピング</p>
        <p className="text-[11.5px] text-zinc-500 mb-3 font-mono">"{row.ocr_value}"</p>
        <p className="text-[10.5px] text-zinc-500 mb-2">
          この OCR 値を既存材質の <span className="font-mono">common_ocr_variants</span> に追加します。
          自動的に新しい材質は作成しません。
        </p>
        <select value={pick} onChange={(e) => setPick(e.target.value)}
          data-testid="unknown-map-picker"
          className="w-full h-10 px-2 rounded-md border border-zinc-200 text-[12.5px]">
          <option value="">— 割り当て先を選択 —</option>
          {materials.map((m) => (
            <option key={m.canonical_code} value={m.canonical_code}>
              {m.canonical_code} — {m.display_name_ja || m.display_name_en || ''}
            </option>
          ))}
        </select>
        {row.suggested_material?.canonical_code && (
          <p className="mt-2 text-[10.5px] text-teal-800">
            推奨: <span className="font-mono">{row.suggested_material.canonical_code}</span>
            （信頼度 {Math.round((row.suggested_material.confidence || 0) * 100)}%）
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}
            data-testid="unknown-map-cancel">キャンセル</Button>
          <Button onClick={save} disabled={busy || !pick}
            className="bg-teal-700 hover:bg-teal-800" data-testid="unknown-map-save">
            {busy ? <CircleNotch size={11} className="animate-spin mr-1" /> : <Check size={11} className="mr-1" />}
            登録
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function UnknownMaterialsPage() {
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapping, setMapping] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, m] = await Promise.all([
        axios.get(`${API}/api/doc-ai/materials/unknown-queue`, { headers: authHeader() }),
        axios.get(`${API}/api/doc-ai/materials`, { headers: authHeader() }),
      ]);
      setRows(q.data.rows || []);
      setMaterials(m.data.rows || []);
    } catch (e) { toast.error('キューの取得に失敗しました'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ignore = async (row, action) => {
    try {
      await axios.post(`${API}/api/doc-ai/materials/unknown-queue/ignore`,
        { ocr_value: row.ocr_value, action },
        { headers: authHeader() });
      toast.success(action === 'ignore' ? '無視しました' : '後で確認に移動しました');
      await load();
    } catch (e) { toast.error('操作に失敗しました'); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-5" data-testid="unknown-materials-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">未登録材質のキュー</h1>
          <p className="text-[12px] text-zinc-600 mt-1">
            OCR が読み取ったが、辞書に一致しなかった材質の候補。
            管理者が明示的に既存材質へ紐付ける必要があります（自動作成はしません）。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/settings/materials" className="text-[12px] text-teal-700 hover:underline"
            data-testid="unknown-goto-materials">材質辞書へ戻る</a>
          <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-[12px]"
            data-testid="unknown-refresh">
            {loading ? <CircleNotch size={13} className="animate-spin mr-1" />
                      : <ArrowsClockwise size={13} className="mr-1" />} 更新
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-6 text-center text-[13px] text-emerald-900"
          data-testid="unknown-empty">
          <Check size={16} weight="fill" className="inline mr-1" />
          未登録材質はありません
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left text-[10.5px] uppercase tracking-wider">
                <th>OCR値</th>
                <th className="w-24">回数</th>
                <th>推奨</th>
                <th className="w-32">最終検出</th>
                <th className="text-right w-56">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r, i) => (
                <tr key={`${r.ocr_value}#${i}`} className="hover:bg-amber-50/30"
                  data-testid={`unknown-row-${i}`}>
                  <td className="px-3 py-2">
                    <span className="font-mono text-[12px] text-zinc-900 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      {r.ocr_value || '（空）'}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums font-semibold text-zinc-900">
                    {r.occurrences.toLocaleString('ja-JP')}
                  </td>
                  <td className="px-3 py-2 text-[11.5px]">
                    {r.suggested_material ? (
                      <span data-testid={`unknown-suggest-${i}`}>
                        <span className="font-mono font-semibold text-teal-800">
                          {r.suggested_material.canonical_code}
                        </span>
                        <span className="ml-1 text-zinc-500 text-[10.5px]">
                          {r.suggested_material.display_name_ja} · {Math.round((r.suggested_material.confidence || 0) * 100)}%
                        </span>
                      </span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-[10.5px] text-zinc-500 tabular-nums">
                    {r.last_seen ? new Date(r.last_seen).toLocaleDateString('ja-JP') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" onClick={() => setMapping(r)}
                      className="h-7 px-2 text-[11px] bg-teal-700 hover:bg-teal-800 mr-1"
                      data-testid={`unknown-map-${i}`}>
                      紐付ける
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => ignore(r, 'review_later')}
                      className="h-7 px-2 text-[11px] mr-1"
                      data-testid={`unknown-later-${i}`}>
                      <ClockCounterClockwise size={11} className="mr-0.5" /> 後で
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => ignore(r, 'ignore')}
                      className="h-7 px-2 text-[11px] text-zinc-500"
                      data-testid={`unknown-ignore-${i}`}>
                      <XCircle size={11} className="mr-0.5" /> 無視
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mapping && (
        <MapDialog row={mapping} materials={materials}
          onClose={() => setMapping(null)}
          onSaved={() => { setMapping(null); load(); }} />
      )}
    </div>
  );
}
